/* eslint-disable react-hooks/exhaustive-deps */
import { useCallback, useEffect, useState } from "react"
import { Outlet, useLocation, useNavigate } from "react-router-dom"
import { useDispatch } from "react-redux"
import { useGetReactNativePayload } from "@hooks/index"
import { getUrlRouteAndParams } from "@app/helpers"
import { isPostPaymentDeepLinkRoute } from "@features/payments/helpers"
import { invalidatePostPaymentCache } from "@features/payments/invalidate-post-payment-cache"
import { setNativePlatform } from "../../../native/utils"
import { resolveNativeDeepLink } from "../native-deep-link"

const GA4_MEASUREMENT_ID = "G-XCSF9E77HL"

declare global {
  interface Window {
    gtag?: (...args: any[]) => void
  }
}

export default function RootPage() {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const location = useLocation()
  const [pageNum, setPageNum] = useState(0)

  useEffect(() => {
    if (!window.gtag) {
      return
    }

    const pagePath = `${location.pathname}${location.search}`

    window.gtag("event", "page_view", {
      send_to: GA4_MEASUREMENT_ID,
      page_path: pagePath,
      page_title: document.title,
      page_location: window.location.href,
    })
  }, [location.pathname, location.search])

  // open external links in native app or browser
  const openExternalUrlInNativeApp = useCallback(
    (url: string) => {
      const { route, params } = getUrlRouteAndParams(url)
      if (isPostPaymentDeepLinkRoute(route)) {
        invalidatePostPaymentCache(dispatch)
      }
      const deepLink = resolveNativeDeepLink(
        route,
        params,
        location.pathname,
        location.search
      )
      if (deepLink.skipNavigate) {
        return
      }
      navigate(deepLink.path, {
        state: deepLink.params,
        replace: deepLink.replace,
      })
    },
    [dispatch, location.pathname, location.search, navigate]
  )

  useGetReactNativePayload("deep_link", openExternalUrlInNativeApp)
  useGetReactNativePayload("platform", setNativePlatform)

  // reduce pageNumber when back is clicked
  const handleReducePageNum = () => {
    if (pageNum > 0) {
      setPageNum(pageNum - 1)
    }
  }
  // navigate to a new page with a back arrow to go back
  // params stored in state is accessed with useLocation()
  const show = (page: string, params: object | null) => {
    setPageNum(pageNum + 1)
    navigate(`/${page}`, { state: params })
  }

  // navigate to a new page with no option to go back on mobile
  // params stored in state is accessed with useLocation()
  const jumpTo = (page: string, params: object | null) => {
    window.history.replaceState({}, "", "/dashboard")
    setPageNum(0)
    navigate(`/${page}`, { state: params })
  }

  return (
    <>
      <Outlet
        context={{
          pageNum,
          show,
          jumpTo,
          handleReducePageNum,
        }}
      />
    </>
  )
}
